'use client'

import { useMemo } from 'react'
import { IconFileDownload, IconSearch } from '@tabler/icons-react'
import clsx from 'clsx'

import { CustomerTypes } from 'modules/customer'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import Tooltip from 'shared/ui/Tooltip/Tooltip'
import { noop } from 'shared/utils/common'
import { parseDateToDDMMYYYY } from 'shared/utils/date'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import EditSaleOrderLabel from '../submodules/editSaleOrder/components/EditSaleOrderLabel'
import { isEditSaleOrderEnabled } from '../submodules/editSaleOrder/utils'
import { downloadSaleOrderDocument } from '../submodules/invoices/utils'
import { SaleOrder } from '../types'
import { isAwaitingPaymentSaleOrder } from '../utils/saleOrders'

import ProfileSaleOrderReturnStatusBadge from './ProfileSaleOrderReturnStatusBadge'
import SaleOrderStatus from './SaleOrderStatus'

import styles from './ProfileSaleOrdersListItemTablet.module.scss'

interface ProfileSaleOrdersListItemTabletProps {
  order: SaleOrder
  user: CustomerTypes
  handlePaymentStart: (order: SaleOrder) => void
  handleRedirect: (path: string) => () => void
  disabledDocuments?: boolean
  token?: string
}
export default function ProfileSaleOrdersListItemTablet({
  order,
  handlePaymentStart,
  handleRedirect,
  disabledDocuments = false,
  token
}: ProfileSaleOrdersListItemTabletProps) {
  const documentsList = useMemo(
    () => [...order.invoices, ...order.receipts],
    [order.invoices, order.receipts]
  )
  const hasDocuments = isNotNilOrEmpty(documentsList)
  const isAwaitingPayment = isAwaitingPaymentSaleOrder(order)

  const showDownloadButton = hasDocuments && token && !isAwaitingPayment

  const showDownloadButtonTooltip = !showDownloadButton && !isAwaitingPayment

  return (
    <div className={styles.column} key={order.id}>
      <div className={styles.cell}>
        <div className={styles.statusWrapper}>
          <SaleOrderStatus status={order.status} />
          {order.returnStatus && (
            <ProfileSaleOrderReturnStatusBadge status={order.returnStatus} />
          )}
        </div>
      </div>
      <div className={clsx(styles.cell, styles.orderTitle)}>Nr zamówienia</div>
      <div className={styles.cell}>#{order.code}</div>
      <div className={clsx(styles.cell, styles.orderTitle)}>Data zamówienia</div>
      <div className={styles.cell}>{parseDateToDDMMYYYY(order.createdAt)}</div>
      <div className={clsx(styles.cell, styles.orderTitle)}>Suma</div>
      <div
        className={styles.cell}
        dangerouslySetInnerHTML={{
          __html: order.paymentValue.formatted
        }}
      />
      <div className={styles.orderActions}>
        {isEditSaleOrderEnabled(order) && (
          <EditSaleOrderLabel
            className={styles.editLabelLayout}
            currentOrder={order}
          />
        )}
        <div className={styles.actionButtonsWrapper}>
          {isAwaitingPayment && (
            <Button
              variant={ButtonVariants.primary}
              onClick={() => handlePaymentStart(order)}
              {...addTestId('profile_order_retry_payment_button')}
            >
              Zapłać
            </Button>
          )}
          {showDownloadButton &&
            !disabledDocuments &&
            documentsList?.map(document => (
              <Button
                key={`${document.id}`}
                id={`order_document_button_${document.id}`}
                variant={ButtonVariants.neutralOutline}
                startIcon={<IconFileDownload />}
                onClick={() => downloadSaleOrderDocument(order, document, token)}
              >
                Dowód zakupu
              </Button>
            ))}
          {showDownloadButtonTooltip && !disabledDocuments && (
            <Tooltip
              id='profile-order-download-button-tooltip'
              title='Dowód zakupu będzie dostępny po dostarczeniu zamówienia'
            >
              <div>
                <Button
                  id='profile_order_receipt_button'
                  variant={ButtonVariants.neutralOutline}
                  startIcon={<IconFileDownload />}
                  onClick={noop}
                  disabled
                >
                  Dowód zakupu
                </Button>
              </div>
            </Tooltip>
          )}
          {isAwaitingPayment}
          <Button
            id='profile_order_details_button'
            variant={ButtonVariants.neutralOutline}
            startIcon={<IconSearch />}
            onClick={handleRedirect(`${PATHS.profileOrders}?order=${order.id}`)}
          >
            Szczegóły
          </Button>
        </div>
      </div>
    </div>
  )
}
